import { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Check, X, Clock, Plus, Search, RefreshCw,
  AlertCircle, MessageSquare, Trash2, CalendarDays, Filter,
  ChevronLeft, ChevronRight, LayoutGrid, List, User
} from 'lucide-react';
import { localApi, ConductorLeave } from '../utils/localApi';

export default function ConductorLeavesManager() {
  const [leaves, setLeaves] = useState<ConductorLeave[]>([]);
  const [usersList, setUsersList] = useState<{ id: string; email: string; display_name?: string; role?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'PENDING' | 'VALIDATED' | 'REJECTED'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'timeline' | 'cards'>('timeline');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // Mois affiché pour le calendrier (par défaut mois actuel)
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Modal détail / action sur congé
  const [selectedLeaveForDetail, setSelectedLeaveForDetail] = useState<ConductorLeave | null>(null);
  const [actionComment, setActionComment] = useState('');

  // Modal nouvelle demande
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newLeaveData, setNewLeaveData] = useState({
    conductor_name: '',
    conductor_id: '',
    leave_type: 'Congés payés',
    start_date: '',
    end_date: '',
    comment: ''
  });
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const fetchLeavesAndUsers = async () => {
    try {
      setLoading(true);
      const [leavesData, usersData] = await Promise.all([
        localApi.getConductorLeaves(),
        localApi.getUsers().catch(() => [])
      ]);
      setLeaves(leavesData || []);
      setUsersList(usersData || []);
    } catch (err) {
      console.error('Erreur chargement congés/utilisateurs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeavesAndUsers();
  }, []);

  const handleUpdateStatus = async (
    leave: ConductorLeave,
    newStatus: 'VALIDATED' | 'REJECTED',
    comment?: string
  ) => {
    try {
      setActionLoadingId(leave.id);
      await localApi.updateConductorLeaveStatus(leave.id, newStatus, comment);
      setLeaves(prev =>
        prev.map(item =>
          item.id === leave.id
            ? { ...item, status: newStatus, comment: comment !== undefined ? comment : item.comment }
            : item
        )
      );
      setSelectedLeaveForDetail(null);
      setActionComment('');
    } catch (err) {
      console.error('Erreur mise à jour statut congé:', err);
      alert('Erreur lors de la mise à jour du statut.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette demande de congé ?')) {
      return;
    }
    try {
      setActionLoadingId(id);
      await localApi.deleteConductorLeave(id);
      setLeaves(prev => prev.filter(l => l.id !== id));
      setSelectedLeaveForDetail(null);
    } catch (err) {
      console.error('Erreur suppression congé:', err);
      alert('Erreur lors de la suppression.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLeaveData.conductor_name || !newLeaveData.start_date || !newLeaveData.end_date) {
      alert('Veuillez renseigner le nom du conducteur et les dates de début et fin.');
      return;
    }
    try {
      setCreateSubmitting(true);
      const newLeave: Partial<ConductorLeave> = {
        conductor_id: newLeaveData.conductor_id || 'driver',
        conductor_name: newLeaveData.conductor_name,
        leave_type: newLeaveData.leave_type,
        start_date: newLeaveData.start_date,
        end_date: newLeaveData.end_date,
        status: 'PENDING',
        comment: newLeaveData.comment
      };
      await localApi.saveConductorLeave(newLeave);
      setIsCreateModalOpen(false);
      setNewLeaveData({
        conductor_name: '',
        conductor_id: '',
        leave_type: 'Congés payés',
        start_date: '',
        end_date: '',
        comment: ''
      });
      await fetchLeavesAndUsers();
    } catch (err) {
      console.error('Erreur création congé:', err);
      alert('Impossible d\'enregistrer la demande de congé.');
    } finally {
      setCreateSubmitting(false);
    }
  };

  // Liste des conducteurs (conducteurs inscrits + ceux ayant des congés)
  const allConductors = useMemo(() => {
    const listMap = new Map<string, string>();

    // 1. Utilisateurs ayant le rôle conducteur ou tous les agents
    usersList.forEach(u => {
      if (!u.role || u.role === 'driver' || u.role === 'conductor') {
        const name = u.display_name || u.email.split('@')[0];
        listMap.set(name, u.id);
      }
    });

    // 2. Conducteurs présents dans les congés
    leaves.forEach(l => {
      if (!listMap.has(l.conductor_name)) {
        listMap.set(l.conductor_name, l.conductor_id || 'driver');
      }
    });

    // Conducteur par défaut si vide
    if (listMap.size === 0) {
      listMap.set('Conducteur Démo', 'driver-cfc');
    }

    return Array.from(listMap.entries()).map(([name, id]) => ({ name, id }));
  }, [usersList, leaves]);

  // Jours du mois affiché
  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month, 1);
    const days: { dateStr: string; dayNum: number; dayName: string; isWeekend: boolean; isToday: boolean }[] = [];

    const todayStr = new Date().toISOString().split('T')[0];

    while (date.getMonth() === month) {
      const dayNum = date.getDate();
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      const dayOfWeek = date.getDay(); // 0 = dimanche, 6 = samedi
      const dayName = date.toLocaleDateString('fr-FR', { weekday: 'narrow' }).toUpperCase();

      days.push({
        dateStr,
        dayNum,
        dayName,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
        isToday: dateStr === todayStr
      });
      date.setDate(date.getDate() + 1);
    }
    return days;
  }, [currentDate]);

  // Navigation calendrier
  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  const handleToday = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
  };

  // Clic sur une cellule vide pour créer directement un congé
  const handleCellClick = (conductorName: string, conductorId: string, dateStr: string) => {
    setNewLeaveData({
      conductor_name: conductorName,
      conductor_id: conductorId,
      leave_type: 'Congés payés',
      start_date: dateStr,
      end_date: dateStr,
      comment: ''
    });
    setIsCreateModalOpen(true);
  };

  const filteredLeaves = useMemo(() => {
    return leaves.filter(leave => {
      const matchesStatus = filterStatus === 'ALL' || leave.status === filterStatus;
      const matchesSearch =
        leave.conductor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (leave.comment || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        leave.leave_type.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [leaves, filterStatus, searchQuery]);

  const pendingCount = useMemo(() => {
    return leaves.filter(l => l.status === 'PENDING').length;
  }, [leaves]);

  const monthLabel = useMemo(() => {
    return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }, [currentDate]);

  const calculateDays = (start: string, end: string) => {
    try {
      const d1 = new Date(start);
      const d2 = new Date(end);
      const diffTime = d2.getTime() - d1.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays > 0 ? diffDays : 1;
    } catch {
      return 1;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* HEADER SECTION */}
      <div className="bg-slate-900/40 rounded-3xl border border-white/5 p-6 backdrop-blur-sm flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20 shadow-inner">
            <CalendarDays className="text-amber-400" size={24} />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-white font-black uppercase tracking-widest text-base">
                Planning des Congés Conducteurs
              </h3>
              {pendingCount > 0 && (
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30 animate-pulse">
                  {pendingCount} en attente
                </span>
              )}
            </div>
            <p className="text-slate-400 text-xs mt-0.5 font-bold uppercase tracking-wider">
              Planning d'équipe interactif et validation des absences
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Bascule de vue */}
          <div className="bg-slate-800/80 p-1 rounded-xl border border-white/5 flex items-center gap-1">
            <button
              onClick={() => setViewMode('timeline')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                viewMode === 'timeline'
                  ? 'bg-cyan-500 text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Vue Planning d'équipe"
            >
              <LayoutGrid size={13} />
              <span>Planning</span>
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all ${
                viewMode === 'cards'
                  ? 'bg-cyan-500 text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Vue Liste / Cartes"
            >
              <List size={13} />
              <span>Liste</span>
            </button>
          </div>

          <button
            onClick={fetchLeavesAndUsers}
            disabled={loading}
            className="p-3 bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl border border-white/5 transition-all active:scale-95"
            title="Rafraîchir"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>

          <button
            onClick={() => {
              setNewLeaveData({
                conductor_name: '',
                conductor_id: '',
                leave_type: 'Congés payés',
                start_date: '',
                end_date: '',
                comment: ''
              });
              setIsCreateModalOpen(true);
            }}
            className="px-4 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-lg shadow-cyan-900/40 transition-all active:scale-95 flex items-center gap-2"
          >
            <Plus size={16} />
            <span>Nouveau Congé</span>
          </button>
        </div>
      </div>

      {/* BARRE DE CONTRÔLE : NAVIGATION TEMPORELLE ET FILTRES */}
      <div className="flex flex-col lg:flex-row items-center justify-between gap-4 bg-slate-900/30 p-4 rounded-2xl border border-white/5">
        {/* Navigation Mois (disponible dans tous les modes, essentielle pour la timeline) */}
        <div className="flex items-center gap-2 w-full lg:w-auto justify-between lg:justify-start">
          <div className="flex items-center gap-1 bg-slate-800/80 p-1 rounded-xl border border-white/5">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-white/10 text-slate-300 hover:text-white rounded-lg transition-colors"
              title="Mois précédent"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1 text-[10px] font-black uppercase tracking-wider text-cyan-400 hover:text-white transition-colors"
            >
              Aujourd'hui
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-white/10 text-slate-300 hover:text-white rounded-lg transition-colors"
              title="Mois suivant"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <h4 className="text-white font-black text-sm uppercase tracking-widest pl-2 capitalize">
            {monthLabel}
          </h4>
        </div>

        {/* Filtres par Statut et Recherche */}
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto justify-end">
          <div className="relative w-full sm:w-56">
            <input
              type="text"
              placeholder="Conducteur, motif..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-slate-800/80 border border-white/10 rounded-xl py-2 pl-8 pr-3 text-xs text-white placeholder-slate-500 outline-none focus:border-cyan-500/50"
            />
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 w-3.5 h-3.5" />
          </div>

          <div className="flex items-center gap-1 bg-slate-800/80 p-1 rounded-xl border border-white/5 overflow-x-auto">
            <button
              onClick={() => setFilterStatus('ALL')}
              className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                filterStatus === 'ALL' ? 'bg-cyan-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => setFilterStatus('PENDING')}
              className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                filterStatus === 'PENDING' ? 'bg-amber-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Attente ({pendingCount})
            </button>
            <button
              onClick={() => setFilterStatus('VALIDATED')}
              className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                filterStatus === 'VALIDATED' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Validés
            </button>
            <button
              onClick={() => setFilterStatus('REJECTED')}
              className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                filterStatus === 'REJECTED' ? 'bg-red-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              Refusés
            </button>
          </div>
        </div>
      </div>

      {/* VUE 1 : PLANNING D'ÉQUIPE MENSUEL (TIMELINE GANTT) */}
      {viewMode === 'timeline' && (
        <div className="bg-slate-900/40 rounded-3xl border border-white/5 overflow-hidden backdrop-blur-md shadow-2xl">
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* EN-TÊTE DU PLANNING : CONDUCTEURS + JOURS */}
              <div className="flex border-b border-white/10 bg-slate-950/60 sticky top-0 z-20">
                <div className="w-48 shrink-0 p-3.5 border-r border-white/10 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Conducteur</span>
                  <span className="text-[9px] font-mono text-cyan-400">Équipe</span>
                </div>
                <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${daysInMonth.length}, minmax(28px, 1fr))` }}>
                  {daysInMonth.map(day => (
                    <div
                      key={day.dateStr}
                      className={`text-center py-2 px-0.5 border-r border-white/5 flex flex-col items-center justify-center transition-colors ${
                        day.isToday
                          ? 'bg-cyan-500/20 border-cyan-500/40 font-bold'
                          : day.isWeekend
                          ? 'bg-black/30'
                          : 'hover:bg-white/5'
                      }`}
                    >
                      <span className={`text-[8px] uppercase tracking-tighter ${day.isToday ? 'text-cyan-400 font-black' : 'text-slate-500'}`}>
                        {day.dayName}
                      </span>
                      <span className={`text-[11px] font-mono font-bold leading-none mt-0.5 ${day.isToday ? 'text-white' : 'text-slate-300'}`}>
                        {day.dayNum}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* LIGNES PAR CONDUCTEUR */}
              <div className="divide-y divide-white/5">
                {allConductors
                  .filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map(conductor => {
                    // Récupérer les congés de ce conducteur pour le mois affiché
                    const conductorLeaves = filteredLeaves.filter(
                      l => l.conductor_name.toLowerCase() === conductor.name.toLowerCase()
                    );

                    return (
                      <div key={conductor.name} className="flex hover:bg-slate-800/20 transition-colors group">
                        {/* Colonne Conducteur */}
                        <div className="w-48 shrink-0 p-3 border-r border-white/10 flex items-center gap-2.5 bg-slate-900/50">
                          <div className="w-7 h-7 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-black text-xs shrink-0">
                            {conductor.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-xs font-bold truncate leading-tight" title={conductor.name}>
                              {conductor.name}
                            </p>
                            <span className="text-[8px] font-mono text-slate-500 uppercase">Agent</span>
                          </div>
                        </div>

                        {/* Grille des Jours du Conducteur avec Barres de Congés */}
                        <div className="flex-1 relative grid" style={{ gridTemplateColumns: `repeat(${daysInMonth.length}, minmax(28px, 1fr))` }}>
                          {/* Cellules cliquables en fond */}
                          {daysInMonth.map(day => (
                            <div
                              key={day.dateStr}
                              onClick={() => handleCellClick(conductor.name, conductor.id, day.dateStr)}
                              className={`h-14 border-r border-white/5 cursor-pointer transition-colors relative group/cell ${
                                day.isToday
                                  ? 'bg-cyan-500/5'
                                  : day.isWeekend
                                  ? 'bg-black/20'
                                  : 'hover:bg-cyan-500/10'
                              }`}
                              title={`Cliquer pour poser un congé pour ${conductor.name} le ${day.dateStr}`}
                            >
                              <div className="opacity-0 group-hover/cell:opacity-100 absolute inset-0 flex items-center justify-center pointer-events-none">
                                <Plus size={12} className="text-cyan-400/50" />
                              </div>
                            </div>
                          ))}

                          {/* Barres de Congés positionnées par-dessus */}
                          {conductorLeaves.map(leave => {
                            // Calcul du positionnement sur la grille
                            const startIdx = daysInMonth.findIndex(d => d.dateStr === leave.start_date);
                            const endIdx = daysInMonth.findIndex(d => d.dateStr === leave.end_date);

                            // Si le congé ne chevauche pas ce mois, on l'ignore ou adapte
                            const monthStartStr = daysInMonth[0].dateStr;
                            const monthEndStr = daysInMonth[daysInMonth.length - 1].dateStr;

                            if (leave.end_date < monthStartStr || leave.start_date > monthEndStr) {
                              return null;
                            }

                            const effectiveStartIdx = startIdx !== -1 ? startIdx : 0;
                            const effectiveEndIdx = endIdx !== -1 ? endIdx : daysInMonth.length - 1;
                            const colSpan = effectiveEndIdx - effectiveStartIdx + 1;

                            const isPending = leave.status === 'PENDING';
                            const isValidated = leave.status === 'VALIDATED';
                            const isRejected = leave.status === 'REJECTED';

                            const leftPercent = (effectiveStartIdx / daysInMonth.length) * 100;
                            const widthPercent = (colSpan / daysInMonth.length) * 100;

                            return (
                              <div
                                key={leave.id}
                                onClick={e => {
                                  e.stopPropagation();
                                  setSelectedLeaveForDetail(leave);
                                }}
                                style={{
                                  left: `${leftPercent}%`,
                                  width: `${widthPercent}%`,
                                  top: '8px',
                                  height: '38px'
                                }}
                                className={`absolute z-10 rounded-xl px-2.5 py-1 border shadow-lg cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-between overflow-hidden ${
                                  isValidated
                                    ? 'bg-emerald-600/30 hover:bg-emerald-600/40 border-emerald-500/50 text-emerald-300'
                                    : isRejected
                                    ? 'bg-red-600/30 hover:bg-red-600/40 border-red-500/50 text-red-300'
                                    : 'bg-amber-600/30 hover:bg-amber-600/40 border-amber-500/50 text-amber-300 animate-pulse'
                                }`}
                                title={`${leave.leave_type} (${leave.start_date} au ${leave.end_date}) - Statut: ${leave.status}`}
                              >
                                <div className="truncate min-w-0 pr-1">
                                  <p className="text-[10px] font-black uppercase tracking-tight truncate leading-tight">
                                    {leave.leave_type}
                                  </p>
                                  <p className="text-[8px] font-mono opacity-80 truncate">
                                    {calculateDays(leave.start_date, leave.end_date)}j • {isPending ? 'En attente' : isValidated ? 'Validé' : 'Refusé'}
                                  </p>
                                </div>
                                <div className="shrink-0">
                                  {isValidated ? (
                                    <Check size={12} strokeWidth={3} className="text-emerald-400" />
                                  ) : isRejected ? (
                                    <X size={12} strokeWidth={3} className="text-red-400" />
                                  ) : (
                                    <Clock size={12} className="text-amber-400" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* LÉGENDE DU PLANNING */}
          <div className="p-4 border-t border-white/5 bg-slate-950/40 flex flex-wrap items-center justify-between gap-4 text-[10px]">
            <div className="flex items-center gap-4">
              <span className="font-bold text-slate-400 uppercase tracking-widest">Légende :</span>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-md bg-amber-500/30 border border-amber-500/60" />
                <span className="text-amber-300 font-bold uppercase">En attente</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-md bg-emerald-500/30 border border-emerald-500/60" />
                <span className="text-emerald-300 font-bold uppercase">Validé</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-md bg-red-500/30 border border-red-500/60" />
                <span className="text-red-300 font-bold uppercase">Refusé</span>
              </div>
            </div>
            <p className="text-slate-500 text-[9px] uppercase tracking-wider">
              💡 Astuce : Cliquez sur une case vide pour poser un congé directement.
            </p>
          </div>
        </div>
      )}

      {/* VUE 2 : CARTES / LISTE DÉTAILLÉE */}
      {viewMode === 'cards' && (
        <>
          {loading ? (
            <div className="text-center py-16 bg-slate-900/20 border border-white/5 rounded-3xl">
              <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin mx-auto mb-3" />
              <p className="text-slate-400 text-xs uppercase tracking-widest font-bold">Chargement des demandes de congés...</p>
            </div>
          ) : filteredLeaves.length === 0 ? (
            <div className="text-center py-16 bg-slate-900/20 border border-white/5 rounded-3xl">
              <Calendar className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-white font-bold text-sm">Aucune demande de congé trouvée</p>
              <p className="text-slate-500 text-xs mt-1">Modifiez vos filtres ou créez une nouvelle demande.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredLeaves.map(leave => {
                const days = calculateDays(leave.start_date, leave.end_date);
                const isPending = leave.status === 'PENDING';
                const isValidated = leave.status === 'VALIDATED';
                const isRejected = leave.status === 'REJECTED';

                return (
                  <div
                    key={leave.id}
                    className="bg-slate-900/60 border border-white/5 hover:border-cyan-500/30 rounded-2xl p-5 flex flex-col justify-between transition-all group backdrop-blur-sm shadow-lg"
                  >
                    <div>
                      {/* Top line: Conducteur & Badge Statut */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <h4 className="text-white font-black text-sm uppercase tracking-wide">
                            {leave.conductor_name}
                          </h4>
                          <span className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider">
                            {leave.leave_type}
                          </span>
                        </div>

                        <span
                          className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider border shrink-0 ${
                            isValidated
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : isRejected
                              ? 'bg-red-500/10 text-red-400 border-red-500/20'
                              : 'bg-amber-500/10 text-amber-400 border-amber-500/20 animate-pulse'
                          }`}
                        >
                          {isValidated ? 'Validé' : isRejected ? 'Refusé' : 'En attente'}
                        </span>
                      </div>

                      {/* Date & Durée */}
                      <div className="bg-slate-800/40 rounded-xl p-3 border border-white/5 space-y-2 mb-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-400 text-[10px] font-bold uppercase">Période</span>
                          <span className="text-cyan-300 font-mono font-bold text-[11px]">
                            {days} jour{days > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs font-mono text-white">
                          <span>{leave.start_date}</span>
                          <span className="text-slate-500">&rarr;</span>
                          <span>{leave.end_date}</span>
                        </div>
                      </div>

                      {/* Commentaire / motif */}
                      {leave.comment && (
                        <div className="bg-black/20 rounded-xl p-2.5 border border-white/5 text-[11px] text-slate-300 mb-4 flex items-start gap-2">
                          <MessageSquare size={13} className="text-slate-500 shrink-0 mt-0.5" />
                          <p className="line-clamp-2 italic">{leave.comment}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="border-t border-white/5 pt-3 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1">
                        <button
                          onClick={() => setSelectedLeaveForDetail(leave)}
                          className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all"
                        >
                          Gérer
                        </button>
                      </div>

                      <button
                        onClick={() => handleDelete(leave.id)}
                        disabled={actionLoadingId === leave.id}
                        className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Supprimer la demande"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* MODAL DÉTAIL / VALIDATION / REFUS DE CONGÉ */}
      {selectedLeaveForDetail && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-white/10 p-6 rounded-3xl shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-4">
              <div>
                <h3 className="text-base font-black uppercase text-white tracking-widest">
                  Gestion du Congé
                </h3>
                <p className="text-xs text-slate-400">
                  Conducteur : <span className="text-cyan-400 font-bold">{selectedLeaveForDetail.conductor_name}</span>
                </p>
              </div>
              <button
                onClick={() => setSelectedLeaveForDetail(null)}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Récapitulatif */}
              <div className="bg-slate-800/40 rounded-xl p-3 border border-white/5 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Type</span>
                  <span className="text-white font-bold">{selectedLeaveForDetail.leave_type}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Dates</span>
                  <span className="font-mono text-cyan-300">
                    {selectedLeaveForDetail.start_date} &rarr; {selectedLeaveForDetail.end_date} ({calculateDays(selectedLeaveForDetail.start_date, selectedLeaveForDetail.end_date)}j)
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Statut actuel</span>
                  <span
                    className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                      selectedLeaveForDetail.status === 'VALIDATED'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : selectedLeaveForDetail.status === 'REJECTED'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-amber-500/20 text-amber-400'
                    }`}
                  >
                    {selectedLeaveForDetail.status}
                  </span>
                </div>
              </div>

              {/* Commentaire optionnel */}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">
                  Commentaire / Motif de décision
                </label>
                <textarea
                  rows={2}
                  defaultValue={selectedLeaveForDetail.comment || ''}
                  onChange={e => setActionComment(e.target.value)}
                  placeholder="Ex: Remplacement validé..."
                  className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white placeholder-slate-600 outline-none focus:border-cyan-500/50"
                />
              </div>

              {/* Boutons d'action */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <button
                  type="button"
                  disabled={actionLoadingId === selectedLeaveForDetail.id}
                  onClick={() =>
                    handleUpdateStatus(
                      selectedLeaveForDetail,
                      'VALIDATED',
                      actionComment.trim() ? actionComment.trim() : selectedLeaveForDetail.comment
                    )
                  }
                  className="py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase tracking-widest text-xs rounded-xl transition-all shadow-lg shadow-emerald-900/40 flex items-center justify-center gap-1.5"
                >
                  <Check size={14} strokeWidth={3} />
                  <span>Valider</span>
                </button>

                <button
                  type="button"
                  disabled={actionLoadingId === selectedLeaveForDetail.id}
                  onClick={() =>
                    handleUpdateStatus(
                      selectedLeaveForDetail,
                      'REJECTED',
                      actionComment.trim() ? actionComment.trim() : selectedLeaveForDetail.comment
                    )
                  }
                  className="py-3 bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-widest text-xs rounded-xl transition-all shadow-lg shadow-red-900/40 flex items-center justify-center gap-1.5"
                >
                  <X size={14} strokeWidth={3} />
                  <span>Refuser</span>
                </button>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <button
                  type="button"
                  onClick={() => handleDelete(selectedLeaveForDetail.id)}
                  className="text-red-400 hover:text-red-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                >
                  <Trash2 size={13} />
                  <span>Supprimer la demande</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedLeaveForDetail(null)}
                  className="text-slate-400 hover:text-white text-xs font-bold uppercase tracking-wider"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CRÉATION DE DEMANDE MANUELLE */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-white/10 p-6 rounded-3xl shadow-2xl w-full max-w-lg animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-4">
              <div>
                <h3 className="text-base font-black uppercase text-white tracking-widest">
                  Poser un Congé Conducteur
                </h3>
                <p className="text-xs text-slate-400">Planification d'une nouvelle absence</p>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-cyan-400 block mb-1">
                  Conducteur *
                </label>
                {allConductors.length > 0 ? (
                  <div className="space-y-1">
                    <select
                      value={newLeaveData.conductor_name}
                      onChange={e => {
                        const found = allConductors.find(c => c.name === e.target.value);
                        setNewLeaveData({
                          ...newLeaveData,
                          conductor_name: e.target.value,
                          conductor_id: found ? found.id : 'driver'
                        });
                      }}
                      className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-cyan-500/50 cursor-pointer"
                    >
                      <option value="">-- Sélectionner un conducteur --</option>
                      {allConductors.map(c => (
                        <option key={c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Ou saisir un autre nom..."
                      value={newLeaveData.conductor_name}
                      onChange={e => setNewLeaveData({ ...newLeaveData, conductor_name: e.target.value })}
                      className="w-full bg-black/20 border border-white/5 rounded-xl p-2.5 text-xs text-slate-300 placeholder-slate-600 outline-none focus:border-cyan-500/50 mt-1"
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    required
                    placeholder="Ex: Jean Dupont"
                    value={newLeaveData.conductor_name}
                    onChange={e => setNewLeaveData({ ...newLeaveData, conductor_name: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white placeholder-slate-600 outline-none focus:border-cyan-500/50"
                  />
                )}
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-cyan-400 block mb-1">
                  Type d'absence
                </label>
                <select
                  value={newLeaveData.leave_type}
                  onChange={e => setNewLeaveData({ ...newLeaveData, leave_type: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-cyan-500/50 cursor-pointer"
                >
                  <option value="Congés payés">Congés payés</option>
                  <option value="RTT">RTT</option>
                  <option value="Repos compensateur">Repos compensateur</option>
                  <option value="Arrêt maladie">Arrêt maladie</option>
                  <option value="Formation">Formation</option>
                  <option value="Autre absence">Autre absence</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-cyan-400 block mb-1">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    required
                    value={newLeaveData.start_date}
                    onChange={e => setNewLeaveData({ ...newLeaveData, start_date: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-cyan-500/50"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-cyan-400 block mb-1">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    required
                    value={newLeaveData.end_date}
                    onChange={e => setNewLeaveData({ ...newLeaveData, end_date: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white outline-none focus:border-cyan-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">
                  Commentaire / Motif
                </label>
                <textarea
                  rows={2}
                  value={newLeaveData.comment}
                  onChange={e => setNewLeaveData({ ...newLeaveData, comment: e.target.value })}
                  placeholder="Détails complémentaires..."
                  className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-xs text-white placeholder-slate-600 outline-none focus:border-cyan-500/50"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-black uppercase tracking-widest text-xs rounded-xl transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={createSubmitting}
                  className="flex-1 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-black uppercase tracking-widest text-xs rounded-xl transition-all shadow-lg shadow-cyan-900/40"
                >
                  {createSubmitting ? 'Enregistrement...' : 'Poser le congé'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
